import { SalesStatus } from "~/features/productDetails/components/PhaseInOut/SalesStatus";
import {
  type SalesCampaignPrice,
  type SalesCampaignProduct,
  type SalesCampaignProductData,
} from "~/domains/salesCampaign/types/SalesCampaign";
import { distinct, groupByFn, toMapFn } from "~/common/utils/reducers";
import { getInStock } from "~/utils/packageEntryUtils";
import { type ProductData } from "~/domains/product/types/ProductData";
import type { ScalingPrice } from "~/domains/pricing/api/PriceInfo";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { selectNetPrice, type SelectNetPriceProps } from "~/domains/product/utils/productDescriptorUtils";
import { shallowEqual, useSelector } from "react-redux";
import { selectSalesPrice } from "~/features/salesCampaign/redux/selectors";
import { zero } from "~/utils/PriceUtils";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";

type CalculationResult<T> =
  | {
      status: "loading";
      data?: T;
    }
  | {
      status: "error";
      data?: T;
    }
  | {
      status: "loaded";
      data: T;
    };

export interface SalesData {
  packCode: string;
  campaignCode: string;
  soldOut: boolean;
  fullySoldOut: boolean;
  isDiscontinued: boolean;
  salesStock: number;
  regularStock: number;
  price?: ScalingPrice;
  actualPrice?: PriceData;
  endDate?: Date;
}

export const calculateSalesCampaign4CampaignPage = ({
  productDescriptor,
  salesCampaignCode,
}: {
  productDescriptor: ProductDescriptor;
  salesCampaignCode: string;
}): CalculationResult<PdpSalesData> => {
  return calculateSalesCampaign4PDP({
    productDescriptor,
    includeSoldOut: true,
    campaignCodes: [salesCampaignCode],
  });
};

// TODO - Change name and remove "PDP" prefix"
export type PdpSalesData = {
  hasSales: boolean;
  salePerPack: { [packCode: string]: PdpSaleData };
};

export type PdpSaleData = SalesData;

/**
 * A lot of code is duplicated between calculateSalesCampaign4CampaignPage and calculateSalesCampaign4PDP
 *
 * TODO - Collapse common code once we are ahead of deadline
 */
export const calculateSalesCampaign4PDP = ({
  productDescriptor,
  includeSoldOut = false,
  campaignCodes,
}: {
  productDescriptor: ProductDescriptor;
  includeSoldOut?: boolean;
  campaignCodes?: string | string[];
}): CalculationResult<PdpSalesData> => {
  const result = getFlatSalesData(productDescriptor);
  if (result.status !== "loaded") {
    return {
      status: result.status,
    };
  }

  const availabilityInfo = productDescriptor.availability!;

  let campaignData;
  if (campaignCodes) {
    const allowSet = new Set(Array.isArray(campaignCodes) ? campaignCodes : [campaignCodes]);
    campaignData = result.data.filter((campaign) => allowSet.has(campaign.code));
  } else {
    campaignData = result.data;
  }

  // Group by pack code - at this level in theory we could have multiple sales campaigns per package...
  const byPackCode = groupByFn(campaignData, (data) => data.packCode);

  const sales = Object.entries(byPackCode)
    .map(([packCode, salesCampaigns]) => {
      // ...but we assume that only one can exist
      // TODO - Handle multiple campaigns for one package case!
      const salesCampaign = salesCampaigns[0];

      const packagingInfo = availabilityInfo.packagingInformation.find((packaging) => packaging.packCode === packCode);
      const salesStock = packagingInfo?.salesStock?.[salesCampaign.code] ?? salesCampaign.quantity ?? 0;

      const hasSalesStock = salesStock > 0;

      const fifteenDigits = productDescriptor.base.fifteenDigit;
      const packagingData = fifteenDigits[packCode];
      const isDiscontinued = packagingData?.salesStatus === SalesStatus.Discontinued;

      const regularStock = packagingInfo ? getInStock(packagingInfo) : 0;
      const hasRegularStock = regularStock > 0;

      return {
        packCode,
        campaignCode: salesCampaign.code,
        // Sold out means there is no sales stock
        soldOut: !hasSalesStock,
        // Fully sold out is when additionally the normal in stock is sold out & package is discontinued
        fullySoldOut: !hasSalesStock && isDiscontinued && !hasRegularStock,
        isDiscontinued,
        regularStock,
        salesStock: salesStock,
        price: salesCampaign.salesCampaignPrice,
        actualPrice: salesCampaign.actualPrice,
        endDate: salesCampaign.endDate,
      };
    })
    .filter((sale) => includeSoldOut || sale.salesStock > 0);

  const salePerPack = toMapFn(sales, (sale) => sale.packCode);

  return {
    status: "loaded",
    data: {
      hasSales: sales.length > 0,
      salePerPack,
    },
  };
};

type FlatCalculationResult = CalculationResult<
  (SalesCampaignProductData & { packCode: string; salesCampaignPrice?: ScalingPrice; actualPrice?: PriceData })[]
>;
function getFlatSalesData(productDescriptor: ProductDescriptor): FlatCalculationResult {
  if (productDescriptor.isLoading || !productDescriptor.isLoaded) {
    return {
      status: "loading",
    };
  }
  if (productDescriptor.isError) {
    return {
      status: "error",
    };
  }

  const salesCampaignsBase = productDescriptor.base.salesCampaign ?? {};

  // Take all the prices, because in case of 15 digit prices, the data is coming on the same price info as the package price
  let salesCampaignsPrice: SalesCampaignPrice = {};
  if (productDescriptor.prices.length) {
    salesCampaignsPrice = Object.assign({}, ...productDescriptor.prices.map((p) => p.salesCampaign).filter(Boolean));
  }

  const flatData = Object.entries(salesCampaignsBase).flatMap(([packCode, data]) => {
    //Flatten price data
    const foundPrice = Object.values(salesCampaignsPrice)
      .flat()
      .find((entry) => entry.packCode === packCode);

    return Object.entries(data).flatMap(([index, entry]) => {
      //And add it if it exists
      return {
        ...entry,
        packCode,
        salesCampaignPrice: foundPrice?.salesCampaignPrice,
        actualPrice: foundPrice?.actualSalesPrice,
      };
    });
  });

  // filter out packages that are not available according to the availability
  const validPackages = productDescriptor.availability?.packagingInformation.map((p) => p.packCode) ?? [];

  const filteredData = flatData.filter((data) => validPackages.includes(data.packCode));

  return {
    status: "loaded",
    data: filteredData,
  };
}

export const productHasAvailableCampaign = (salesCampaignData: Record<string, SalesCampaignProductData[]>) => {
  return (
    Object.values(salesCampaignData)
      //In the future product can be part of more than one campaign at the same time
      .flatMap((campaignEntry) => campaignEntry)
      .some((saleData) => saleData.quantity > 0)
  );
};
export const productHasSoldOutCampaign = (product: ProductData) => {
  return (
    Object.values(product.salesCampaign)
      //In the future product can be part of more than one campaign at the same time
      .flatMap((campaignEntry) => campaignEntry)
      .some((saleData) => saleData.quantity === 0)
  );
};

export const getProductCampaignStatus = (salesCampaignData: Record<string, SalesCampaignProductData[]>) => {
  //TODO: Refactor productHasAvailableCampaign function calls to getProductCampaignStatus when/if scope expands?
  const flatData = Object.values(salesCampaignData).flatMap((campaignEntry) => campaignEntry);

  let hasSale = false;
  let isSoldOut = false;

  if (flatData.some((saleData) => saleData.quantity > 0)) {
    hasSale = true;
  } else if (flatData.some((saleData) => saleData.quantity === 0)) {
    hasSale = isSoldOut = true;
  }

  return { hasSale, isSoldOut };
};

export const getCampaignCodesFromProduct = (salesCampaign?: SalesCampaignProduct) => {
  if (salesCampaign) {
    return distinct(
      Object.values(salesCampaign)
        .flatMap((campaignEntry) => campaignEntry)
        .map((item) => item.code),
    );
  } else {
    return [];
  }
};

interface UseSelectPricingDetailsReturnValues {
  price: PriceData | null;
  oldPrice: PriceData | null;
  netPrice: NetPriceData | null;
  salesPrice: PriceData | null;
  salesPriceIsBetter: boolean;
  discountFormatted: string | null;
}

export function useSelectPricingDetails({
  packCode,
  quantity,
  priceReference,
  productCode,
  salesData,
  saleActivated,
}: {
  packCode: string;
  quantity: number;
  priceReference: string;
  productCode: string;
  salesData: SalesData | undefined;
  saleActivated: boolean;
}): UseSelectPricingDetailsReturnValues {
  const priceParams: SelectNetPriceProps = {
    packCode,
    extraQuantity: quantity,
    priceReference: priceReference,
    productCode,
  };
  const regularPrice = useSelector(selectNetPrice(priceParams), shallowEqual);
  const salesPrice = useSelector(selectSalesPrice({ ...priceParams, salesData }), shallowEqual) ?? zero();

  return processPricingDetails({
    netPrice: regularPrice,
    salesPrice,
    saleActivated,
  });
}

export function getDiscountFormatted(regularPrice: PriceData | null, salesPrice: PriceData | null): string | null {
  const regularPriceIsValid = regularPrice && regularPrice.value > 0;
  const salesPriceIsValid = salesPrice && salesPrice.value > 0;
  const salesPriceIsBetter = salesPriceIsValid && (!regularPriceIsValid || salesPrice.value < regularPrice.value);

  let discountFormatted = null;
  if (regularPriceIsValid && salesPriceIsBetter) {
    const diff = regularPrice.value - salesPrice.value;
    const discountPercent = Math.round((diff / regularPrice.value) * 100);
    discountFormatted = `-${NF.getWeightFormatterG().format(discountPercent)}%`;
  }

  return discountFormatted;
}

export function processPricingDetails({
  netPrice,
  salesPrice,
  saleActivated,
}: {
  netPrice: NetPriceData | null;
  salesPrice: PriceData | null;
  saleActivated: boolean;
}) {
  if (!saleActivated) {
    return {
      price: netPrice,
      netPrice,
      oldPrice: null,
      salesPrice,
      salesPriceIsBetter: false,
      discountFormatted: null,
    };
  }

  const regularPriceIsValid = !!netPrice && netPrice.value > 0;
  const salesPriceIsValid = !!salesPrice && salesPrice.value > 0;
  const salesPriceIsBetter = salesPriceIsValid && (!regularPriceIsValid || salesPrice.value < netPrice.value);

  const discountFormatted = getDiscountFormatted(netPrice, salesPrice);

  return {
    price: salesPriceIsBetter ? salesPrice : netPrice,
    oldPrice: salesPriceIsBetter && regularPriceIsValid ? netPrice : null,
    netPrice,
    salesPrice,
    salesPriceIsBetter,
    discountFormatted,
  };
}
